import React, { useRef, useCallback } from 'react';
import { Button, type ButtonProps } from '@mui/material';
import { gsap } from 'gsap';

export type ButtonAnimationVariant = 'scale' | 'pop-rotate' | 'spin-burst' | 'none';

export interface AnimatedButtonProps extends Omit<ButtonProps, 'variant'> {
  /** MUI button variant */
  muiVariant?: ButtonProps['variant'];
  /** Animation variant for interactions */
  animationVariant?: ButtonAnimationVariant;
  /** Icon element to animate separately */
  icon?: React.ReactNode;
  /** Icon position */
  iconPosition?: 'start' | 'end';
  /** Children/button content */
  children: React.ReactNode;
}

/**
 * AnimatedButton - Atomic button with opt-in GSAP animations
 * 
 * Built on MUI Button with optional animation variants.
 * 
 * Layer 1: Atomic Component
 * - Pure presentation, no domain knowledge
 * - Wraps MUI Button with animation support
 * 
 * Animation Variants:
 * - 'none': No animations (default)
 * - 'scale': Simple scale on hover/click
 * - 'pop-rotate': Elastic pop with rotation on click
 * - 'spin-burst': 360° rotation burst with wiggle on hover
 * 
 * @example Basic button with scale animation
 * ```tsx
 * <AnimatedButton animationVariant="scale">
 *   Click me
 * </AnimatedButton>
 * ```
 * 
 * @example Button with pop-rotate animation
 * ```tsx
 * <AnimatedButton 
 *   animationVariant="pop-rotate"
 *   icon={<Icon />}
 *   iconPosition="start"
 * >
 *   Button Text
 * </AnimatedButton>
 * ```
 * 
 * @example Button with spin-burst animation
 * ```tsx
 * <AnimatedButton 
 *   animationVariant="spin-burst"
 *   icon={<Icon />}
 *   iconPosition="start"
 * >
 *   Button Text
 * </AnimatedButton>
 * ```
 */
export function AnimatedButton({
  muiVariant = 'contained',
  animationVariant = 'none',
  icon,
  iconPosition = 'start',
  children,
  onClick,
  ...props
}: AnimatedButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const iconRef = useRef<HTMLSpanElement>(null);

  // Scale animation (simple hover/click)
  const handleScaleMouseEnter = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1.05,
        duration: 0.3,
        ease: 'back.out(1.7)',
      });
    }
  }, []);

  const handleScaleMouseLeave = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  }, []);

  // Pop-rotate animation - elastic pop with rotation on click
  const handlePopRotateClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (iconRef.current) {
      gsap.timeline()
        .to(iconRef.current, {
          scale: 1.4,
          rotation: 15,
          duration: 0.2,
          ease: 'back.out(2)',
        })
        .to(iconRef.current, {
          scale: 1,
          rotation: 0,
          duration: 0.3,
          ease: 'elastic.out(1, 0.5)',
        });
    }
    onClick?.(e);
  }, [onClick]);

  // Spin-burst animation - 360° rotation burst with wiggle on hover
  const handleSpinBurstMouseEnter = useCallback(() => {
    if (buttonRef.current) {
      gsap.to(buttonRef.current, {
        scale: 1.05,
        duration: 0.3,
        ease: 'back.out(1.7)',
      });
    }
    
    // Icon wiggle
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        rotation: 15,
        duration: 0.15,
        ease: 'power2.out',
        yoyo: true,
        repeat: 1,
      });
    }
  }, []);

  const handleSpinBurstClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (iconRef.current) {
      gsap.timeline()
        .to(iconRef.current, {
          scale: 1.5,
          rotation: 360,
          duration: 0.4,
          ease: 'back.out(2)',
        })
        .to(iconRef.current, {
          scale: 1,
          rotation: 0,
          duration: 0.3,
          ease: 'elastic.out(1, 0.5)',
        });
    }
    onClick?.(e);
  }, [onClick]);

  // Select handlers based on animation variant
  const getHandlers = () => {
    switch (animationVariant) {
      case 'scale':
        return {
          onMouseEnter: handleScaleMouseEnter,
          onMouseLeave: handleScaleMouseLeave,
          onClick,
        };
      case 'pop-rotate':
        return {
          onMouseEnter: handleScaleMouseEnter,
          onMouseLeave: handleScaleMouseLeave,
          onClick: handlePopRotateClick,
        };
      case 'spin-burst':
        return {
          onMouseEnter: handleSpinBurstMouseEnter,
          onMouseLeave: handleScaleMouseLeave,
          onClick: handleSpinBurstClick,
        };
      default:
        return { onClick };
    }
  };

  const iconElement = icon ? (
    <span 
      ref={iconRef}
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center',
        willChange: 'transform',
      }}
    >
      {icon}
    </span>
  ) : null;

  return (
    <Button
      ref={buttonRef}
      variant={muiVariant}
      {...getHandlers()}
      {...props}
      sx={{
        willChange: animationVariant !== 'none' ? 'transform' : undefined,
        ...props.sx,
      }}
    >
      {iconPosition === 'start' && iconElement}
      {children}
      {iconPosition === 'end' && iconElement}
    </Button>
  );
}

export default AnimatedButton;

// Export individual variants for direct use
export { ScaleButton, PopRotateButton, SpinBurstButton } from './variants';
export type { ScaleButtonProps, PopRotateButtonProps, SpinBurstButtonProps } from './variants';
